import * as Api from '@/api';
import { getClassrooms, GoogleClassroomCourse } from '@/api/auth/google';
import firebase from 'firebase';
import { ActionContext } from 'vuex';
import { defineModule, localActionContext } from 'direct-vuex';
import { GettingStartedPeerGroup, MemberRole } from '@/models';
import * as Utils from '@/utils';
import { BasePacket, Badge } from '@/models/packet';
import { AxiosError } from 'axios';

export enum UserCreateStatus {
  READY,
  IN_PROGRESS,
  CREATE_POSSIBLE,
  ALREADY_EXISTS,
  AUTH_FAILED
}

export enum WorkflowStep {
  SIGN_IN,
  SET_NAME,
  CHOOSE_GO_TO_EXISTING_ORG_OR_CREATE,
  CREATE_ORG,
  CHOOSE_GOOGLE_CLASSROOM_OR_MANUAL,
  PICK_GOOGLE_CLASSROOM_COURSE,
  BUILD_GROUP,
  FIND_OR_CREATE_GOALS_BRANCH,
  CREATE_GOALS,
  FIND_GOALS,
  NEW_GOAL_KIND_BRANCH,
  BUILD_SINGLE_POINT_GOALS,
  BUILD_MULTI_POINT_GOALS,
  REVIEW,
  FINISH_OR_RETRY
}

export enum GroupBuilderKind {
  GOOGLE_CLASSROOM,
  MANUAL
}

export type GroupBuilder = ManualGroupBuilder | GoogleClassroomBuilder;

interface GoogleClassroomBuilder {
  kind: GroupBuilderKind.GOOGLE_CLASSROOM;
  courses: GoogleClassroomCourse[];
  selected: GoogleClassroomCourse | null;
}

export interface ManualGroupBuilder {
  kind: GroupBuilderKind.MANUAL;
  name: string;
  description: string;
  section: string;
  members: {
    email: string;
    role: MemberRole;
    valid: boolean;
    timestamp: number;
  }[];
}

const emptyManualGroupBuilder: ManualGroupBuilder = Object.freeze({
  kind: GroupBuilderKind.MANUAL,
  name: '',
  description: '',
  section: '',
  members: []
});

const emptyGoogleClassroomBuilder: GoogleClassroomBuilder = Object.freeze({
  kind: GroupBuilderKind.GOOGLE_CLASSROOM,
  courses: [],
  selected: null,
  code: null
});

export enum GoalsType {
  NEW,
  PUBLIC
}

export type PacketInfo = [BasePacket, Badge[]];
export type PacketInfoOption = [BasePacket, Badge[], boolean];

export interface CreationProcess {
  isInProgress: boolean;
  current: string;
  complete: string[];
  failed: string[];
  userConflicts: string[];
  newOrgId?: number;
}

interface State {
  // (Maybe?) Used to skip extra authentication callbacks from the dashboard.
  isSigningIn: boolean;

  // Tracks which pricing element the user clicked on www.unrulr
  accountType: string;
  step: WorkflowStep;
  firebaseUser: firebase.User | null;
  hasAccount: boolean;
  name: string;
  existingPeerGroups: GettingStartedPeerGroup[];
  userCreateStatus: UserCreateStatus;
  orgName: string;
  groupBuilder: GroupBuilder;
  goalsType: GoalsType;
  publicPacketOptions: PacketInfoOption[];
  newPacket: [BasePacket, Badge[]] | null;
  creationProcess: CreationProcess;
}

const getInitialState = (): State => {
  return {
    isSigningIn: false,
    accountType: 'unsetType',
    step: WorkflowStep.SIGN_IN,
    firebaseUser: null,
    hasAccount: false,
    name: '',
    userCreateStatus: UserCreateStatus.READY,
    orgName: '',
    groupBuilder: emptyManualGroupBuilder,
    goalsType: GoalsType.PUBLIC,
    publicPacketOptions: [],
    existingPeerGroups: [],
    newPacket: null,
    creationProcess: {
      // Not really used
      isInProgress: false,

      // The current message to display
      current: 'Ready',

      // The steps which have been completed
      complete: [],

      // Steps which have yet to complete when an error occurs
      failed: [],

      // Emails which are already registered with other organizations;
      userConflicts: [],

      // Whether we have reached a point where we can send them on
      // to the admin system regardless of whether or not subsequent
      // requests fail
      newOrgId: undefined
    }
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getStartedActionContext = (context: any) =>
  // eslint-disable-next-line  @typescript-eslint/no-use-before-define
  localActionContext(context, store);

const attemptAccountCreation = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: ActionContext<any, any>,
  fetchFirebaseUser: () => Promise<firebase.User | null>
) => {
  const { commit } = getStartedActionContext(context);

  commit.setUserCreateStatus(UserCreateStatus.IN_PROGRESS);
  commit.setIsSigningIn(true);

  try {
    // The main.ts created() callback should not trigger when this is called,
    // as long as store.isSigningIn === true;
    const user = await fetchFirebaseUser();
    const idToken = await user?.getIdToken();

    if (!user || !idToken) {
      // Either the user or the idToken is null (should never be possible because
      // signInWithPopup should throw on failure, but is still typed as
      // (null | user) and (null | string))
      throw new Error(
        'Somehow we got a null/undefined idToken while signing in with Google + Firebase?'
      );
    }

    commit.setFirebaseUser(user);
    commit.updateManualGroup({
      members: user.email
        ? [
            {
              email: user.email,
              role: MemberRole.TEACHER,
              valid: true,
              timestamp: Date.now()
            }
          ]
        : []
    });

    try {
      const tokens = await Api.auth.exchangeIdToken(idToken, 'get-started');

      commit.setHasAccount(true);
      // The user exists in our system, so we need to ask if they meant
      // to edit/view an existing org/group, or create a new org+group
      Api.auth.setAccessToken(tokens.accessToken);
      localStorage.setItem('unrulr_refresh_token', tokens.refreshToken);

      const peerGroups = await Api.secure.getPeerGroups(true);

      commit.setExistingPeerGroups(peerGroups);

      commit.setStep(WorkflowStep.CHOOSE_GO_TO_EXISTING_ORG_OR_CREATE);
      commit.updateManualGroup({});
    } catch (error) {
      const e = error as AxiosError;
      if (e.isAxiosError && e.response?.status === 401) {
        // The user does _not_ already exist in our system, so we can jump
        // to the org creation screen
        commit.setHasAccount(false);
        commit.setUserCreateStatus(UserCreateStatus.CREATE_POSSIBLE);
        commit.setStep(WorkflowStep.CHOOSE_GO_TO_EXISTING_ORG_OR_CREATE);
      } else {
        console.error(error);

        // Checking on the user account failed in some other way
        commit.setUserCreateStatus(UserCreateStatus.AUTH_FAILED);
      }
    }
  } catch (error) {
    console.error(error);
    // Firebase sign in failed
    commit.setUserCreateStatus(UserCreateStatus.AUTH_FAILED);
  }

  commit.setIsSigningIn(false);
};

const store = defineModule({
  namespaced: true as true,

  state: getInitialState(),

  mutations: {
    setIsSigningIn: (state, isSigningIn: boolean) => {
      state.isSigningIn = isSigningIn;
    },

    setAccountType: (state, accountType: string) => {
      state.accountType = accountType;
    },

    setFirebaseUser: (state, user: firebase.User) => {
      state.firebaseUser = user;
    },

    setHasAccount: (state, hasAccount) => {
      state.hasAccount = hasAccount;
    },

    setName: (state, name: string) => {
      state.name = name;
    },

    setUserCreateStatus: (state, ucs: UserCreateStatus) => {
      state.userCreateStatus = ucs;
    },

    setStep: (state, step: WorkflowStep) => {
      state.step = step;
    },

    setExistingPeerGroups: (state, groups: GettingStartedPeerGroup[]) => {
      state.existingPeerGroups = groups;
    },

    setOrgName: (state, name: string) => {
      state.orgName = name;
    },

    setGoogleClassroomCourses: (state, courses: GoogleClassroomCourse[]) => {
      state.groupBuilder = {
        ...emptyGoogleClassroomBuilder,
        courses
      };
    },

    setGoogleClassroomCourse: (
      state,
      { course }: { course: GoogleClassroomCourse }
    ) => {
      const existing = state.groupBuilder;

      if (existing.kind === GroupBuilderKind.GOOGLE_CLASSROOM) {
        state.groupBuilder = {
          ...existing,
          selected: course
        };
      } else {
        console.error(
          'Trying to select a google course while not in the google course workflow'
        );
      }
    },

    initManualGroup: state => {
      state.groupBuilder = {
        ...emptyManualGroupBuilder
      };
    },

    updateManualGroup: (state, group: Partial<ManualGroupBuilder>) => {
      const existing = state.groupBuilder;

      if (existing.kind === GroupBuilderKind.MANUAL) {
        state.groupBuilder = {
          ...existing,
          ...group
        };
      } else {
        console.error(
          'Trying to update a manual group while not in the manual group workflow'
        );
      }
    },

    initPacketOptions: (state, packetInfo: PacketInfo[]) => {
      state.publicPacketOptions = packetInfo.map(([p, b]) => [p, b, false]);
    },

    togglePublicPacket: (state, packet: BasePacket) => {
      state.publicPacketOptions = Utils.updateFirstWhere(
        state.publicPacketOptions,
        ([p]) => p.packetId === packet.packetId,
        ([p, b, isSelected]): PacketInfoOption => [p, b, !isSelected]
      );
    },

    setGoalsType: (state, type: GoalsType) => {
      state.goalsType = type;
    },

    setNewPacket: (state, newPacket: PacketInfo) => {
      state.newPacket = newPacket;
    },

    creationProcessSetUserConflicts: (state, emails: string[]) => {
      state.creationProcess = {
        ...state.creationProcess,
        userConflicts: emails
      };
    },

    creationProcessSetCurrent: (state, message: string) => {
      state.creationProcess = {
        ...state.creationProcess,
        current: message
      };
    },

    creationProcessCompleteStep: (state, newlyComplete: string) => {
      state.creationProcess = {
        ...state.creationProcess,
        complete: [...state.creationProcess.complete, newlyComplete]
      };
    },

    creationProcessStart: state => {
      state.creationProcess = {
        ...state.creationProcess,
        isInProgress: true
      };
    },

    creationProcessStop: state => {
      state.creationProcess = {
        ...state.creationProcess,
        isInProgress: false
      };
    },

    creationProcessSetFailed: (state, incompleteSteps: string[]) => {
      state.creationProcess = {
        ...state.creationProcess,
        failed: incompleteSteps
      };
    },

    creationProcessSetCanAdvance: (state, newOrgId: number) => {
      state.creationProcess = {
        ...state.creationProcess,
        newOrgId
      };
    }
  },

  actions: {
    setAccountType(context, accountType: string) {
      getStartedActionContext(context).commit.setAccountType(accountType);
    },

    goToStep(context, step: WorkflowStep) {
      const { commit } = getStartedActionContext(context);

      commit.setStep(step);
    },

    setOrgName(context, name: string) {
      const { commit } = getStartedActionContext(context);

      commit.setOrgName(name);
    },

    async getStartedWithGoogle(context, idToken) {
      return attemptAccountCreation(context, () =>
        Api.auth.signInWithGoogle(idToken)
      );
    },

    async requestEmailSignupCode(context, email: string) {
      const { commit } = getStartedActionContext(context);
      commit.setUserCreateStatus(UserCreateStatus.IN_PROGRESS);

      try {
        await Api.auth.requestSignupCode(email);
        commit.setUserCreateStatus(UserCreateStatus.READY);
      } catch (error) {
        console.error(error);
        commit.setUserCreateStatus(UserCreateStatus.AUTH_FAILED);
      }
    },

    async getStartedWithEmailCode(
      context,
      { email, code }: { email: string; code: string }
    ) {
      const { commit } = getStartedActionContext(context);
      commit.setUserCreateStatus(UserCreateStatus.IN_PROGRESS);
      commit.setIsSigningIn(true);

      try {
        const tokens = await Api.auth.exchangeEmailCodeForToken(email, code);
        localStorage.setItem('unrulr_refresh_token', tokens.refreshToken);
        Api.auth.setAccessToken(tokens.accessToken);

        const userInfo = await Api.auth.userInfo();

        commit.updateManualGroup({
          members: [
            {
              email: userInfo.email,
              role: MemberRole.TEACHER,
              valid: true,
              timestamp: Date.now()
            }
          ]
        });

        const peerGroups = await Api.secure.getPeerGroups(true);
        commit.setExistingPeerGroups(peerGroups);
        commit.setHasAccount(true);

        if (!userInfo.name || userInfo.name.trim() === '') {
          commit.setStep(WorkflowStep.SET_NAME);
        } else {
          commit.setStep(WorkflowStep.CHOOSE_GO_TO_EXISTING_ORG_OR_CREATE);
        }
      } catch (error) {
        console.error(error);
        commit.setUserCreateStatus(UserCreateStatus.AUTH_FAILED);
      }

      commit.setIsSigningIn(false);
    },

    async getStartedWithMagicLink(context, token: string) {
      const { commit } = getStartedActionContext(context);
      commit.setUserCreateStatus(UserCreateStatus.IN_PROGRESS);
      commit.setIsSigningIn(true);

      try {
        const tokens = await Api.auth.exchangeMagicLinkForToken(token);
        localStorage.setItem('unrulr_refresh_token', tokens.refreshToken);
        Api.auth.setAccessToken(tokens.accessToken);

        const userInfo = await Api.auth.userInfo();

        commit.updateManualGroup({
          members: [
            {
              email: userInfo.email,
              role: MemberRole.TEACHER,
              valid: true,
              timestamp: Date.now()
            }
          ]
        });

        const peerGroups = await Api.secure.getPeerGroups(true);
        commit.setExistingPeerGroups(peerGroups);
        commit.setHasAccount(true);

        if (!userInfo.name || userInfo.name.trim() === '') {
          commit.setStep(WorkflowStep.SET_NAME);
        } else {
          commit.setStep(WorkflowStep.CHOOSE_GO_TO_EXISTING_ORG_OR_CREATE);
        }
      } catch (error) {
        console.error(error);
        commit.setUserCreateStatus(UserCreateStatus.AUTH_FAILED);
      }

      commit.setIsSigningIn(false);
    },

    async setUserName(context, name: string) {
      const { commit } = getStartedActionContext(context);
      commit.setUserCreateStatus(UserCreateStatus.IN_PROGRESS);

      try {
        const userInfo = await Api.auth.userInfo();
        await Api.auth.updateName(userInfo.userId, name);
        commit.setName(name);
        commit.setStep(WorkflowStep.CHOOSE_GO_TO_EXISTING_ORG_OR_CREATE);
      } catch (error) {
        console.error(error);
        commit.setUserCreateStatus(UserCreateStatus.AUTH_FAILED);
      }

      commit.setUserCreateStatus(UserCreateStatus.READY);
    },

    async getStartedWithMicrosoft(context) {
      return attemptAccountCreation(context, Api.auth.signInWithMicrosoft);
    },

    async getGoogleClassrooms(context) {
      const { commit } = getStartedActionContext(context);

      try {
        commit.setIsSigningIn(true);

        const courses = await getClassrooms();

        commit.setGoogleClassroomCourses(courses);
        commit.setStep(WorkflowStep.PICK_GOOGLE_CLASSROOM_COURSE);
      } catch (error) {
        console.error(error);
        commit.setGoogleClassroomCourses([]);
      }
      commit.setIsSigningIn(false);
    },

    async selectGoogleClassroomCourse(context, course: GoogleClassroomCourse) {
      const { commit } = getStartedActionContext(context);

      try {
        commit.setIsSigningIn(true);
        commit.setGoogleClassroomCourse({ course });
        commit.setStep(WorkflowStep.FIND_OR_CREATE_GOALS_BRANCH);
      } catch (error) {
        console.error(error);
      }

      commit.setIsSigningIn(false);
    },

    usePublicGoals(context) {
      const { commit } = getStartedActionContext(context);

      commit.setGoalsType(GoalsType.PUBLIC);
    },

    createNewGoals(context) {
      const { commit } = getStartedActionContext(context);

      commit.setGoalsType(GoalsType.NEW);
    },

    async getPublicPackets(context) {
      const { commit } = getStartedActionContext(context);

      const packets = await Api.public.getPublicPackets();

      commit.initPacketOptions(packets);
    },

    togglePacket(context, packet: BasePacket) {
      const { commit } = getStartedActionContext(context);

      commit.togglePublicPacket(packet);
    },

    finishCreatePacket(context, packetInfo: PacketInfo) {
      const { commit } = getStartedActionContext(context);

      commit.setNewPacket(packetInfo);
    },

    startManualGroupBuild(context) {
      const { commit, state } = getStartedActionContext(context);

      if (state.groupBuilder.kind === GroupBuilderKind.MANUAL) {
        return;
      }

      commit.initManualGroup();
      commit.updateManualGroup({
        members: state.firebaseUser?.email
          ? [
              {
                email: state.firebaseUser.email,
                role: MemberRole.TEACHER,
                valid: true,
                timestamp: Date.now()
              }
            ]
          : []
      });
    },

    updateManualGroup(context, groupInfo: Partial<ManualGroupBuilder>) {
      const { commit } = getStartedActionContext(context);

      commit.updateManualGroup(groupInfo);
    },

    // Steps to persist via the api:
    // 1. Create the user, if they do not exist
    // 2. Create the org
    // 3. Create the packet, if the packet was new
    // 4. Create the peer group
    //   a. If a Google Classroom, sync with the classroom (includes, teachers and students)
    //   b. If a manually created group, create the group, and then for each member:
    //     i. Check that the member is _not_ a member of another group
    //     ii. Add the member to the organization
    //     iii. Add the member to the group
    // 5. Connect the new group to the packets (either existing or created)
    async create(context) {
      const { commit, state } = getStartedActionContext(context);

      commit.creationProcessStart();

      try {
        if (!state.firebaseUser && !state.hasAccount) {
          console.error(
            'Somehow trying to finish up without a firebaseUser or existing account?'
          );
          return;
        }

        if (!state.hasAccount && state.firebaseUser) {
          commit.creationProcessSetCurrent('Creating your user account');
          const idToken = await state.firebaseUser.getIdToken();

          const accessToken = await Utils.atLeast(
            Api.public.createUser(idToken)
          );

          Api.auth.setAccessToken(accessToken);
        }

        const userInfo = await Api.auth.userInfo();

        if (!state.hasAccount && state.firebaseUser) {
          commit.creationProcessCompleteStep('Created your user account');
        }

        commit.creationProcessSetCurrent('Creating the organization');
        const organization = await Utils.atLeast(
          Api.secure.createOrganization(
            state.orgName,
            userInfo.userId,
            state.accountType
          )
        ).catch(e => {
          commit.creationProcessSetFailed([
            'Create organization',
            'Create group',
            'Build and connect goals'
          ]);
          throw e;
        });

        commit.creationProcessCompleteStep('Created the organization');
        // As soon as the user has created an organization and been assigned as an
        // admin, we can send them to the org screen, regardless of whether or not
        // we successfully create the group and packets.
        commit.creationProcessSetCanAdvance(organization.organizationId);

        // Track the users who are already in another organization.
        const userConflicts = [];

        let peerGroupId: number;

        if (state.groupBuilder.kind === GroupBuilderKind.MANUAL) {
          commit.creationProcessSetCurrent('Creating the group');

          const peerGroup = await Utils.atLeast(
            Api.secure.createPeerGroup(
              organization.organizationId,
              state.groupBuilder
            )
          ).catch(e => {
            commit.creationProcessSetFailed([
              'Create a new group',
              'Build and connect cogs'
            ]);

            throw e;
          });

          commit.creationProcessCompleteStep('Created new group');
          peerGroupId = peerGroup.peerGroupId;

          commit.creationProcessSetCurrent('Adding users to the group');
          for (let i = 0; i < state.groupBuilder.members.length; i++) {
            const { email: rawEmail, role } = state.groupBuilder.members[i];

            // We normalize here to prevent a subtle bug:
            //
            // When a user is created, we normalize the email in the system to lowercase.  So
            // if a user inputs an email with uppercase characters, the email which comes back
            // from the upsert call _will not match_ the input email.  Which will cause us to
            // skip the addUserGroupConnection call.
            //
            // When that happens, the user ends up with a new group with zero members.
            const memberEmail = rawEmail.toLowerCase();

            try {
              const [user] =
                // If the email belongs to the active user, they've already been
                // added to the org, so just reuse their user object.
                userInfo.emails.some(ue => ue.email === memberEmail)
                  ? [userInfo]
                  : (
                      await Api.secure.upsertUserIntoOrganization(
                        organization.organizationId,
                        memberEmail
                      )
                    ).filter(u =>
                      u.emails.some(ue => ue.email === memberEmail)
                    );

              await Api.secure.addUserGroupConnection(
                user.userId,
                peerGroupId,
                role
              );
            } catch (error) {
              const e = error as AxiosError;

              if (e.isAxiosError) {
                if (e.response?.status === 422) {
                  userConflicts.push(memberEmail);
                }
              }

              console.error(error);
            }
          }

          commit.creationProcessCompleteStep('Added users');
          commit.creationProcessSetUserConflicts(userConflicts);
        } else {
          const { selected } = state.groupBuilder;

          if (!selected) {
            throw new Error(
              'No google classroom course selected while trying to finish up!'
            );
          }

          commit.creationProcessSetCurrent('Syncing with Google Classroom');

          const peerGroup = await Utils.atLeast(
            Api.secure.importClassroom(
              organization.organizationId,
              selected.id,
              2,
              true
            )
          ).catch(e => {
            commit.creationProcessSetFailed([
              'Sync Google Classroom',
              'Build and connect cogs'
            ]);

            throw e;
          });

          commit.creationProcessCompleteStep('Synced with Google Classroom');

          peerGroupId = peerGroup.peerGroupId;
        }

        let packetIds: number[] = [];

        if (state.goalsType === GoalsType.PUBLIC) {
          packetIds = state.publicPacketOptions
            .filter(([, , selected]) => selected)
            .map(([p]) => p.packetId);
        } else {
          if (!state.newPacket) {
            throw Error('goalsType === CREATE, but missing newPacket');
          }

          commit.creationProcessSetCurrent('Building your cogs');
          const [packet, badges] = state.newPacket;

          await Utils.atLeast(
            Api.secure.createPacketWithBadges(
              organization.organizationId,
              packet,
              badges,
              packet => (packetIds = [packet.packetId]),
              () => null
            )
          ).catch(e => {
            commit.creationProcessSetFailed(['Build and connect cogs']);

            throw e;
          });

          commit.creationProcessCompleteStep('Built cogs');
        }

        commit.creationProcessSetCurrent('Connecting cogs');

        for (let i = 0; i < packetIds.length; i++) {
          const packetId = packetIds[i];

          await Api.secure
            .connectPeerGroupToPacket(peerGroupId, packetId)
            .catch(e => {
              commit.creationProcessSetFailed(['Connect all cogs']);

              throw e;
            });
        }

        commit.creationProcessSetCurrent('');
        commit.creationProcessCompleteStep('Connected cogs');

        await Utils.sleep(500);
      } catch (error) {
        commit.creationProcessSetCurrent('');
        console.error(error);
      }

      commit.creationProcessStop();
      commit.setStep(WorkflowStep.FINISH_OR_RETRY);
    }
  }
});

export default store;
